/**
 * NP-TRIAGE-PERSIST-001 — New Product triage composition (mirrors PHP NpTriageCompositionRules).
 */
import { STEP_LIBRARY } from '@/lib/dd-queue/data/steps'

export const NP_TRIAGE_LOCKED_KEYS = [
  'design',
  'estimation',
  'sales_approval',
  'sku',
  'product_review',
  'pim_publish',
] as const

export type NpTriageLockedKey = (typeof NP_TRIAGE_LOCKED_KEYS)[number]

/** Each pair requires at least one selected. */
export const NP_TRIAGE_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['production_file', 'outsource_file'],
]

export const NP_TRIAGE_PAIR_HINT =
  'At least one of Pre-press / Vendor File is required'

export function npTriageAllowedKeys(): string[] {
  return STEP_LIBRARY.filter((s) => s.key !== 'pm_assessment').map((s) => s.key)
}

export function isNpTriageLockedKey(key: string): boolean {
  return (NP_TRIAGE_LOCKED_KEYS as readonly string[]).includes(key)
}

export function isNpTriagePairKey(key: string): boolean {
  return NP_TRIAGE_PAIRS.some((pair) => pair.includes(key))
}

/** True when every member of some pair is unselected. */
export function npTriagePairViolated(
  map: Record<string, boolean>,
  pairs: ReadonlyArray<readonly [string, string]> = NP_TRIAGE_PAIRS
): boolean {
  for (const [a, b] of pairs) {
    if (!(map[a] ?? false) && !(map[b] ?? false)) return true
  }
  return false
}

/** True when unchecking `key` would leave its pair with zero selected. */
export function wouldViolateNpTriagePair(
  map: Record<string, boolean>,
  key: string,
  nextValue: boolean
): boolean {
  if (nextValue) return false
  const pair = NP_TRIAGE_PAIRS.find((p) => p.includes(key))
  if (!pair) return false
  return npTriagePairViolated({ ...map, [key]: false }, [pair])
}

/** Sole selected member of its pair — locked until the mate is re-checked. */
export function isNpTriageSoleSelectedPairMember(
  map: Record<string, boolean>,
  key: string
): boolean {
  for (const pair of NP_TRIAGE_PAIRS) {
    if (!pair.includes(key)) continue
    const other = pair[0] === key ? pair[1] : pair[0]
    return Boolean(map[key]) && !map[other]
  }
  return false
}

/** Hint while a pair member is sole-locked or the pair is fully unselected. */
export function npTriagePairHintForMap(map: Record<string, boolean>): string | null {
  if (npTriagePairViolated(map)) return NP_TRIAGE_PAIR_HINT
  for (const pair of NP_TRIAGE_PAIRS) {
    if (pair.some((k) => isNpTriageSoleSelectedPairMember(map, k))) {
      return NP_TRIAGE_PAIR_HINT
    }
  }
  return null
}

/** Force locked-required keys on. Does not silently repair a violated pair. */
export function forceNpTriageLockedSelected(
  map: Record<string, boolean>
): Record<string, boolean> {
  const next = { ...map }
  for (const key of NP_TRIAGE_LOCKED_KEYS) {
    next[key] = true
  }
  return next
}
